// Route guards.
//   <RequireAuth> ensures the user is logged in.
//   <RequireView view="market" /> ensures the user can see the requested view.
//   <RequireClient clientId={...} /> ensures the user can see this client.

import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const LoadingShell = () => (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <div className="text-sm text-[hsl(var(--mute))]">Loading…</div>
    </div>
);

export const RequireAuth = () => {
    const { status } = useAuth();
    const location = useLocation();
    if (status === "loading") return <LoadingShell />;
    if (status !== "authed") {
        return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
    }
    return <Outlet />;
};

export const RequireView = ({ view, children }) => {
    const { user, canMarket, canClient, isAdmin } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (isAdmin) return children || <Outlet />;
    if (view === "market" && !canMarket) return <AccessDenied requested="Market view" />;
    if (view === "client" && !canClient) return <AccessDenied requested="Client view" />;
    return children || <Outlet />;
};

export const RequireClient = ({ clientId, children }) => {
    const { user, canAccessClient, isAdmin } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (!canAccessClient(clientId)) {
        // Soft redirect when the route was inherited from a previous session
        // (e.g. logged out as UBS, logged back in as LBBW with /client/ubs in
        // the URL). Send the user somewhere they can actually go.
        if (!isAdmin) {
            const allowed = (user.allowedClientIds || [])[0];
            const target = allowed ? `/client/${allowed}` : "/";
            return <Navigate to={target} replace />;
        }
        return <AccessDenied requested={`Client: ${clientId}`} />;
    }
    return children || <Outlet />;
};

export const AccessDenied = ({ requested }) => (
    <section className="card-flat p-10 max-w-xl mx-auto text-center" data-testid="access-denied">
        <div className="eyebrow text-[hsl(var(--warning))]">Access denied</div>
        <h2 className="display text-2xl mt-2 leading-snug">You do not have access to this view</h2>
        {requested && (
            <p className="text-sm text-[hsl(var(--mute))] mt-3">
                Requested: <span className="text-[hsl(var(--ink-soft))] font-medium">{requested}</span>
            </p>
        )}
        <p className="text-sm text-[hsl(var(--ink-soft))] mt-4 leading-relaxed">
            Your current account does not have permission to open this dashboard. If you believe this is a
            mistake, contact your Zühlke relationship manager.
        </p>
    </section>
);
